import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { playChime, unlockAudio } from "@/lib/sound";

const supported = () => typeof window !== "undefined" && "Notification" in window;

export function usePermissionState() {
  const [state, setState] = useState(supported() ? Notification.permission : "unsupported");
  const request = async () => {
    if (!supported()) return "unsupported";
    const r = await Notification.requestPermission();
    setState(r);
    return r;
  };
  return { state, request, supported: supported() };
}

export function useAssignmentAlerts() {
  const { user, impersonating } = useAuth();
  const navigate = useNavigate();
  const sinceRef = useRef(null);
  const promptedRef = useRef(false);

  useEffect(() => {
    if (!user?.id || impersonating) return undefined;
    const key = `tt_notif_since_${user.id}`;
    sinceRef.current = localStorage.getItem(key) || new Date().toISOString();
    localStorage.setItem(key, sinceRef.current);
    if (supported() && Notification.permission === "default" && !promptedRef.current && !sessionStorage.getItem("tt_notif_prompted")) {
      promptedRef.current = true;
      sessionStorage.setItem("tt_notif_prompted", "1");
      toast("Turn on desktop notifications?", {
        description: "Know instantly when a ticket lands on you, even while you're in another tab.",
        duration: 15000,
        action: { label: "Enable", onClick: () => Notification.requestPermission().then((r) => toast[r === "granted" ? "success" : "info"](r === "granted" ? "Desktop notifications are on" : "You can enable them later from the bell in the top bar")) },
      });
    }
    let stopped = false;
    const unlock = () => { unlockAudio(); window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock); };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    const poll = async () => {
      try {
        const { data } = await api.get("/me/notifications", { params: { since: sinceRef.current } });
        if (stopped) return;
        if (data.items.length) playChime();
        for (const n of data.items) {
          const open = () => navigate(`/tickets/${n.ticket_id}`);
          toast.info(`${n.number} assigned to you`, { description: `${n.subject} · ${n.channel}`, action: { label: "Open", onClick: open }, duration: 12000 });
          if (supported() && Notification.permission === "granted") {
            const nt = new Notification(`${n.number} assigned to you`, { body: `${n.subject}\n${n.channel} · ${n.reason}`, tag: n.id, icon: "/favicon.ico" });
            nt.onclick = () => { window.focus(); open(); nt.close(); };
          }
        }
        sinceRef.current = data.now;
        localStorage.setItem(key, data.now);
      } catch { /* offline or signed out — try again on the next tick */ }
    };
    poll();
    const id = setInterval(poll, 20000);
    return () => { stopped = true; clearInterval(id); window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock); };
  }, [user?.id, impersonating, navigate]);
}
